import {currentRegistry, type PricingFacts, type SportsRegistry} from '../sports/registry';
import {pricingFacts} from './plans';

/**
 * Payments v3 (payments_v3_enabled) and the $2.99 Plus trial (plus_trial_enabled), as the client
 * says them. Every number comes from the facts (GET /sports → pricing.payments_v3 / plus_trial,
 * PricingConfig::publicFacts), never typed here; the arithmetic mirrors App\Services\Payments\
 * AllInFees exactly so the Payments step shows what the charge will take.
 */

export function paymentsV3On(registry: SportsRegistry = currentRegistry()): boolean {
    return registry.payments_v3 === true;
}

export function plusTrialOn(registry: SportsRegistry = currentRegistry()): boolean {
    return registry.plus_trial === true;
}

export type AllInRow = {rate: number; fixed_cents: number; plus_rate: number};

/** The all-in row for a charge currency, or the fallback currency's. */
export function allInRow(currency: string | null | undefined, f: PricingFacts = pricingFacts()): AllInRow & {currency: string; listed: boolean} {
    const table = f.payments_v3?.all_in_fees ?? {};
    const cc = (currency || 'USD').toUpperCase();
    const fallback = (f.payments_v3?.fallback_currency || 'USD').toUpperCase();
    const row = table[cc] ?? table[fallback] ?? {rate: 0, fixed_cents: 0, plus_rate: 0};
    return {...row, currency: cc, listed: !!table[cc]};
}

/** The organiser's bank country, as PaymentsV3::bankCountryOf reads it: the one chosen for payouts, else their own. */
export function bankCountryOf(u: {meta?: {payments_bank_country?: string | null} | null; country_code?: string | null} | null | undefined): string | null {
    const c = u?.meta?.payments_bank_country || u?.country_code;
    return c ? String(c).toUpperCase() : null;
}

/** Is Express offered for this country? */
export function expressOffered(country: string | null | undefined, f: PricingFacts = pricingFacts()): boolean {
    return !!country && (f.payments_v3?.express_countries ?? []).includes(country.toUpperCase());
}

const SYMBOLS: Record<string, string> = {GBP: '£', EUR: '€', USD: '$', AUD: 'A$', CAD: 'C$', NZD: 'NZ$'};

/** "£0.40", "A$0.60": a minor-unit amount in its currency, the way the fee lines print it. */
export function moneyFor(cents: number, currency: string): string {
    const cc = currency.toUpperCase();
    const n = (cents / 100).toFixed(2);
    return SYMBOLS[cc] ? `${SYMBOLS[cc]}${n}` : `${cc} ${n}`;
}

/** "3% + 40p" for GBP, "4.5% + $0.80" for USD — the all-in fee in words. */
export function allInLine(currency: string | null | undefined, plus = false, f: PricingFacts = pricingFacts()): string {
    const r = allInRow(currency, f);
    const rate = plus ? r.plus_rate : r.rate;
    const fixed = r.currency === 'GBP' && r.fixed_cents < 100 ? `${r.fixed_cents}p` : moneyFor(r.fixed_cents, r.currency);
    return `${rateText(rate)}% + ${fixed}`;
}

function rateText(rate: number): string {
    return Number.isInteger(rate) ? String(rate) : String(Math.round(rate * 100) / 100);
}

/**
 * One registration payment's arithmetic (AllInFees::quote): absorb — the player pays the entry,
 * the organiser nets entry − fee; pass — the fee is a booking fee on the player's price and the
 * organiser nets the entry.
 */
export function quote(amountCents: number, currency: string | null | undefined, plus = false, mode: 'absorb' | 'pass' = 'absorb', f: PricingFacts = pricingFacts()) {
    const r = allInRow(currency, f);
    const rate = plus ? r.plus_rate : r.rate;
    const fee = amountCents > 0 ? Math.round(amountCents * rate / 100) + r.fixed_cents : 0;
    const charged = mode === 'pass' ? amountCents + fee : amountCents;
    return {
        fee_cents:           fee,
        booking_fee_cents:   mode === 'pass' ? fee : 0,
        player_pays_cents:   charged,
        organiser_net_cents: mode === 'pass' ? amountCents : amountCents - fee,
        currency:            r.currency,
        rate,
        fixed_cents:         r.fixed_cents,
    };
}

export const PAYMENTS_V3 = {
    /** The Payments step: "Online entry fees: 3% + 40p all-in, no monthly cost. On £20 you receive £19.00." */
    stepLine: (amountCents: number, currency: string | null | undefined, plus = false, f: PricingFacts = pricingFacts()): string => {
        const q = quote(amountCents, currency, plus, 'absorb', f);
        const head = `Online entry fees: ${allInLine(currency, plus, f)} all-in, no monthly cost.`;
        return amountCents > 0 ? `${head} On ${moneyFor(amountCents, q.currency)} you receive ${moneyFor(q.organiser_net_cents, q.currency)}.` : head;
    },
    /** The same fee, as the Billing page, the pricing page and the FAQ say it. */
    feeSentence: (currency: string | null | undefined, f: PricingFacts = pricingFacts()): string =>
        `Online entry fees are ${allInLine(currency, false, f)} all-in, with no monthly cost; on Plus ${allInLine(currency, true, f)}. Stripe's processing is included — the organiser pays nothing else.`,
    absorbLabel: 'I pay the fee — the player sees the entry price',
    passLabel: 'Add it to the player\'s price as a booking fee',
    /** What the player sees at checkout when the fee is passed on. */
    checkoutLine: (amountCents: number, feeCents: number, currency: string): string =>
        `Entry ${moneyFor(amountCents, currency)} + ${moneyFor(feeCents, currency)} booking fee`,
    collectTitle: 'Collect on the day — ScorePal tracks who\'s paid',
    collectBody: 'Online payment isn\'t offered for your country yet. Every registration records the entry fee as owed; mark players paid from the Players tab as the money comes in.',
    connectBody: 'Connect Stripe and entry fees are paid online at registration; until then players register and pay on the day, and ScorePal tracks who has paid.',
};

export const PLUS_TRIAL = {
    /** "Try Plus — $2.99 for 14 days, everything unlocked, then $39/month. Cancel anytime." */
    title: (f: PricingFacts = pricingFacts()): string => {
        const t = f.plus_trial;
        return `Try Plus — ${usd(t?.fee_cents ?? 299)} for ${t?.days ?? 14} days, everything unlocked, then ${usd(t?.monthly_cents ?? f.plus_monthly_cents)}/month. Cancel anytime.`;
    },
    /** The consent line that sits NEXT TO the button, never in a footnote. */
    thenLine: (f: PricingFacts = pricingFacts()): string => `Then ${usd(f.plus_trial?.monthly_cents ?? f.plus_monthly_cents)}/month after ${f.plus_trial?.days ?? 14} days unless you cancel.`,
    cta: (f: PricingFacts = pricingFacts()): string => `Try Plus — ${usd(f.plus_trial?.fee_cents ?? 299)}`,
    justThisEvent: (price: string): string => `Just this event — Event Pass ${price}`,
    alreadyUsed: 'Plus trial already used',
    activeTitle: (endsOn: string): string => `Plus trial — everything unlocked until ${endsOn}`,
    cancelledTitle: (endsOn: string): string => `Plus trial cancelled — still unlocked until ${endsOn}, then Free`,
    cancel: 'Cancel the trial',
    cancelDone: 'Trial cancelled. Everything stays unlocked until it ends; nothing will be charged.',
    sidebar: (f: PricingFacts = pricingFacts()): string => `Try Plus — ${usd(f.plus_trial?.fee_cents ?? 299)}`,
};

function usd(cents: number): string {
    const n = cents / 100;
    return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}
/** The one-time Event Pass offer's code box (pass_offer_enabled): shown while the offer is on. */
export function passOfferOn(f: PricingFacts = pricingFacts()): boolean {
    return f.pass_offer?.enabled === true;
}

/** The code box's words. The discount comes from the server's check, never typed here. */
export const PASS_OFFER = {
    haveCode:    'Have a code?',
    placeholder: 'SP-XXXXXX',
    apply:       'Apply',
    applied:     (percent: number, discounted: string, full: string) => `${percent}% off applied — ${discounted} instead of ${full}`,
    remove:      'Remove',
};
